import React, { HTMLProps, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { MIJNPENSIOENOVERZICHT_URL, PENSION_ROBOT_MPO_VIDEO_LINK } from '../../../config/constants';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { PensionDataType } from '../../../types/PensionDataType';
import { toPrice } from '../../../utils/parsers';
import { Button } from '../../general/Button';
import { Dropzone } from '../../general/DropZone';
import { ModalScreenPension } from '../../general/ModalScreen';
import { SummaryEntry } from '../../investment-robot/forecast-screen/ForecastResultsCalculations';
import { OverlayHeader } from '../OverlayHeader';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { SavingButtons } from './DialogueDataSaving/PensionProgressSaver';
import { BackButton, NextButton } from './NavigationButtons';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';
import { isPensionCalculatedTogether } from '../../../utils/functions';
import { Link } from '../../general/Link';

const { XMLParser } = require('fast-xml-parser');

export const PensionSection: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');

    const {
        parameters: { personalInfo, pensionInfo },
    } = useContext(PensionContext);

    const isCalculatedTogether = isPensionCalculatedTogether(personalInfo);
    const isMarried = personalInfo.maritalStatus === 'married';

    const pensionDataSelfIsKnown = () => {
        const hasAow = isMarried
            ? !!pensionInfo?.self?.aowGrossYearly?.together
            : !!pensionInfo?.self?.aowGrossYearly?.single;
        const hasEmployeePension = !!pensionInfo?.self?.employeePensionGrossYearly;
        return hasAow && hasEmployeePension;
    };

    const pensionDataPartnerisKnown = () => {
        const hasAow = !!pensionInfo?.partner?.aowGrossYearly;
        const hasEmployeePension = !!pensionInfo?.partner?.employeePensionGrossYearly;
        return hasAow && hasEmployeePension;
    };

    const dataForThisStepIsKnown = () => {
        return isPartnerSection ? pensionDataPartnerisKnown() : pensionDataSelfIsKnown();
    };

    // Assumption: only called if next but is enabled, so can assume dataForThisStepIsKnown is true
    const getNextPage = () => {
        const selfPensionInfoIsFilledIn = !isPartnerSection && isCalculatedTogether;
        if (selfPensionInfoIsFilledIn) return 'pension-partner';

        return 'supplement';
    };

    return (
        <CocktailWrapperScreen
            page={3}
            title={
                isPartnerSection
                    ? t('pension-cocktail-fill-screen-pension-partner-title')
                    : t('pension-cocktail-fill-screen-pension-title')
            }
            subTitle={
                isPartnerSection
                    ? t('pension-cocktail-fill-screen-pension-partner-subsubtitle')
                    : t('pension-cocktail-fill-screen-pension-subsubtitle')
            }
        >
            <PensionSelectionButtons isPartnerSection={isPartnerSection} />
            <SmallLetters className={'my-8'} />
            <XMLModal isPartnerSection={isPartnerSection} />

            <div className="flex flex-row flex-wrap gap-4 items-start my-8">
                <BackButton />
                <NextButton nextPage={getNextPage()} isDisabled={!dataForThisStepIsKnown()} />
            </div>
            <SavingButtons />
        </CocktailWrapperScreen>
    );
};

const PensionSelectionButtons: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const { actions } = useContext(PensionContext);
    const history = useHistory();
    return (
        <div className="flex flex-col gap-4 my-8">
            <div className="flex flex-row flex-wrap gap-4 items-center">
                <span className="opacity-50 ml-4 w-24">{`2 ${t('general-minutes')}`}</span>
                <Button
                    className="px-4 sm:px-8"
                    buttonType="primary"
                    onClick={() => actions.setParameters({ pensionInfo: { modal: { show: true } } })}
                >
                    {t('pension-cocktail-fill-screen-pension-btn-xml')}
                </Button>
            </div>
            <div className="flex flex-row flex-wrap gap-4 items-center">
                <span className="opacity-50 ml-4 w-24">{`10 ${t('general-minutes')}`}</span>
                <Button
                    className="mr-16 px-4 sm:px-8 h-10"
                    buttonType="secondary"
                    onClick={() => {
                        history.push(isPartnerSection ? 'pension-aow-partner' : 'pension-aow');
                    }}
                >
                    {isPartnerSection
                        ? t('pension-cocktail-fill-screen-pension-partner-btn-insert-self')
                        : t('pension-cocktail-fill-screen-pension-btn-insert-self')}
                </Button>
            </div>
        </div>
    );
};

const SmallLetters: React.FC<HTMLProps<HTMLDivElement>> = ({ className }) => {
    const { t } = useTranslation('pension');
    return (
        <div className={className}>
            <p className="font-medium text-sm">{t('pension-cocktail-fill-screen-pension-small-letters-title')}</p>
            <p className="text-sm">{t('pension-cocktail-fill-screen-pension-small-letters-text')}</p>
        </div>
    );
};

const XMLModal: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { pensionInfo, personalInfo },
        actions,
    } = useContext(PensionContext);
    const history = useHistory();
    const breakpoints = useWindowBreakpoints();

    const [pensionInfoFromXml, setPensionInfoFromXml] = useState({} as any);
    const [xmlFileName, setXmlFileName] = useState('');

    enum xmlParseStatusStyle {
        Success = 'bg-bnd-green border-bnd-green/10',
        Fail = 'bg-bnd-red/10',
        Waiting = 'bg-bnd-black/10',
    }
    const [xmlParseStatus, setXmlParseStatus] = useState<xmlParseStatusStyle>(xmlParseStatusStyle.Waiting);
    const hasPartner = personalInfo.maritalStatus === 'married';

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            acceptedFiles.forEach((file) => {
                const reader = new FileReader();
                const parser = new XMLParser();

                reader.onload = () => {
                    try {
                        const xmlData: PensionDataType = parser.parse(reader.result);

                        const pensionTotals =
                            xmlData.PensioenGegevensType.Totalen.OuderdomsPensioenTotalen.OuderdomsPensioenTotaal;

                        // We only use the last values ( from age x to deceased ). If there is only one entry,
                        // the parser doesn't see it as a list but just a single value. So we have to check if
                        // if it's a list, then take the last one, of if it's not a list there is only one entry
                        // and thus use that one.
                        const pensionTotal = Array.isArray(pensionTotals)
                            ? pensionTotals[pensionTotals.length - 1]
                            : pensionTotals;
                        const aowAlleenstaand = (pensionTotal.AOWAlleenstaand ?? 0) * 100;
                        const aowSamenwonend = (pensionTotal.AOWSamenwonend ?? 0) * 100;
                        const employeePensionGrossYearly =
                            ((pensionTotal.Pensioen ?? 0) + (pensionTotal.IndicatiefPensioen ?? 0)) * 100;

                        setXmlFileName(file.name);
                        if (isPartnerSection) {
                            setPensionInfoFromXml({
                                employeePensionGrossYearly: employeePensionGrossYearly,
                                aowGrossYearly: aowSamenwonend,
                            });
                        } else {
                            setPensionInfoFromXml({
                                employeePensionGrossYearly: employeePensionGrossYearly,
                                aowGrossYearly: {
                                    single: aowAlleenstaand,
                                    together: aowSamenwonend,
                                },
                            });
                        }
                        setXmlParseStatus(xmlParseStatusStyle.Success);
                    } catch (e: unknown) {
                        setXmlFileName(t('my-pension-modal-error'));
                        setXmlParseStatus(xmlParseStatusStyle.Fail);
                    }
                };
                reader.readAsText(file);
            });
        },
        [isPartnerSection, xmlParseStatusStyle.Success, xmlParseStatusStyle.Fail, t],
    );

    return (
        <ModalScreenPension isShown={!!pensionInfo?.modal?.show}>
            <div className="bg-white md:rounded-xl p-4 sm:p-8">
                <OverlayHeader
                    title={
                        breakpoints.isMobile || breakpoints.isTablet
                            ? isPartnerSection
                                ? t('my-pension-modal-partner-title-sm')
                                : t('my-pension-modal-title-sm')
                            : isPartnerSection
                            ? t('my-pension-modal-partner-title')
                            : t('my-pension-modal-title')
                    }
                    onClose={() => {
                        actions.setParameters({ pensionInfo: { modal: { show: false } } });
                    }}
                />
                <div className="flex flex-col gap-4 mt-8">
                    <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                        <p className="font-medium text-lg">
                            {t('my-pension-modal-subtitle')}{' '}
                            <Link
                                className="underline"
                                href={MIJNPENSIOENOVERZICHT_URL}
                                rel="noreferrer"
                                target="_blank"
                            >
                                {t('my-pension-modal-website')}
                            </Link>
                            . {t('my-pension-modal-subtitle-2')}
                        </p>
                        <div style={{ flex: 6 }}></div>
                    </div>

                    <div className="vimeo-player-container">
                        <iframe title="bnd-mpo-video" src={PENSION_ROBOT_MPO_VIDEO_LINK}></iframe>
                    </div>
                </div>

                <span>{t('my-pension-modal-content')}</span>
                <Dropzone
                    className={'mt-6 mb-6 ' + xmlParseStatus}
                    text={xmlFileName ? xmlFileName : t('my-pension-modal-dropzone')}
                    dragText={t('my-pension-modal-dropzone-dragging')}
                    btn={xmlFileName ? 'XML opnieuw uploaden' : t('my-pension-modal-dropzone-btn')}
                    ondrop={onDrop}
                />
                {Object.keys(pensionInfoFromXml).length !== 0 && (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                            <div className="mb-4">
                                <SummaryEntry
                                    text={t('my-pension-modal-employeepension-label')}
                                    value={toPrice(pensionInfoFromXml.employeePensionGrossYearly, {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}
                                />
                                <SummaryEntry
                                    text={t('my-pension-modal-aow-label')}
                                    className={'mt-2'}
                                    value={toPrice(
                                        isPartnerSection
                                            ? pensionInfoFromXml.aowGrossYearly
                                            : hasPartner
                                            ? pensionInfoFromXml.aowGrossYearly.together
                                            : pensionInfoFromXml.aowGrossYearly.single,
                                        {
                                            onlyFullEuros: true,
                                            withCurrencySymbol: true,
                                        },
                                    )}
                                />
                            </div>
                        </div>

                        <div className="mt-2">
                            <Button
                                className="flex flex-row px-8"
                                buttonType="primary"
                                onClick={() => {
                                    actions.setParameters(
                                        isPartnerSection
                                            ? { pensionInfo: { partner: pensionInfoFromXml } }
                                            : {
                                                  pensionInfo: { self: pensionInfoFromXml },
                                              },
                                    );
                                    actions.setParameters({
                                        pensionInfo: { modal: { show: false } },
                                    });
                                    setPensionInfoFromXml({});
                                    setXmlFileName('');
                                    setXmlParseStatus(xmlParseStatusStyle.Waiting);
                                    history.push(
                                        isPartnerSection || !isPensionCalculatedTogether(personalInfo)
                                            ? 'supplement'
                                            : 'pension-partner',
                                    );
                                }}
                            >
                                {t('my-pension-modal-btn')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </ModalScreenPension>
    );
};
